import { Box, type BoxProps } from '@mui/material';
import { CatchBoundary } from '@tanstack/react-router';
import React from 'react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  type TreeNode,
  type TreeNodeState,
  useCreateNode,
  useEditorActiveNode,
  useEditorContext,
  useNodeState,
} from '../Editor';
import { getNodeById } from '../utils/utils';

type Props<NS extends TreeNodeState = TreeNodeState> = {
  children:
    | ((props: {
        id: string;
        state: NS;
        setState: TreeNode<NS>['setState'];
        setStateDebounced: TreeNode<NS>['setStateDebounced'];
      }) => React.ReactNode)
    | React.ReactNode;
  node?: Omit<Parameters<typeof useCreateNode<NS>>[0]['node'], 'nodePath' | 'parentId'>;

  onStateChange?: (state: NS) => void;
  onClick?: () => void;
  onUnmount?: (
    node: TreeNode<NS> | null,
    store: NonNullable<ReturnType<typeof useEditorContext>>['store'] | null
  ) => void;
} & StylingProps;

type StylingProps = {
  className?: string;
  style?: React.CSSProperties;
  sx?: BoxProps['sx'];
};

type UINodeContextType = {
  node: TreeNode | null;
};

/**
 * Ui Node Context
 * This is used to automatically assign the parent node id to the child node.
 */
export const UiNodeContext = createContext<UINodeContextType | null>(null);

export const UiNode = <NS extends TreeNodeState = TreeNodeState>({ children, ...props }: Props<NS>) => {
  const editorCtx = useEditorContext();
  const { activeNode, setActiveNode } = useEditorActiveNode<NS>();
  const [node] = useCreateNode<NS>({ node: { ...props?.node } });

  // keep track of the node
  const nodeRef = useRef(node);
  useEffect(() => {
    nodeRef.current = node;
  }, [node]);

  // unmount
  useEffect(() => {
    return () => {
      props?.onUnmount?.(nodeRef.current, editorCtx?.store ?? null);
    };
  }, []);

  const nodeState = useNodeState<NS>(node?.id);
  useEffect(() => {
    if (node?.config?.readOnly === true) return;

    if (props?.onStateChange) {
      props?.onStateChange?.(nodeState);
    }
  }, [nodeState]);

  /**
   * Check if the node is active.
   */
  const [isActive, _setIsActive] = useState(false);
  const setIsActive = useCallback((value: boolean) => {
    if (isActiveRef.current === value) {
      return;
    }

    isActiveRef.current = value;
    _setIsActive(value);
  }, []);
  const isActiveRef = useRef(false);

  useEffect(() => {
    if (editorCtx?.config?.readOnly === true || node?.config?.readOnly === true) {
      return;
    }

    if (!node) {
      return;
    }

    if (!activeNode?.nodePath) {
      setIsActive(false);
      return;
    }

    if (activeNode?.nodePath?.includes(node?.nodePath ?? '')) {
      setIsActive(true);
      return;
    }

    setIsActive(false);
  }, [activeNode?.nodePath, node?.nodePath]);

  /**
   * traverse the event tree upwards to find the current node. and stop propagation.
   */
  const htmlTargetTraversal = useCallback(
    (e: React.FocusEvent | React.MouseEvent) => {
      if (editorCtx?.config?.readOnly || node?.config?.readOnly) {
        return;
      }

      if (!e?.target) {
        return;
      }

      const uuid = (e?.currentTarget as HTMLElement)?.dataset.uuid;
      if (!uuid) {
        return;
      }

      let tNode = e?.target as HTMLElement | null;
      while (tNode && tNode.getAttribute('data-uuid') !== uuid) {
        tNode = tNode?.parentElement;
      }

      if (tNode && tNode.getAttribute('data-uuid') === uuid) {
        setIsActive(true);
        setActiveNode(node);
        e.stopPropagation();
      }
    },
    [setActiveNode, setIsActive, node, editorCtx?.config?.readOnly]
  );

  return (
    <CatchBoundary
      getResetKey={() => 'reset'}
      // onCatch={(error) => console.error(error, node)}
      errorComponent={({ error, reset }) => (
        <div>
          <p>Something went wrong in UiNode {node?.id}</p>
          <pre>{error.message}</pre>
          <button type="button" onClick={reset}>
            Try again
          </button>
        </div>
      )}
    >
      <UiNodeContext.Provider value={{ node: node as TreeNode }}>
        <Box
          className={`pue-ui-node pue-ui-node-${node?.id} ${node?.config?.alias ? `pue-ui-node-alias-${node.config.alias.replaceAll(' ', '_')}` : ''} pue-ui-node-${isActive ? 'active' : 'inactive'} ${props?.className ?? ''}`}
          data-uuid={node?.id}
          onFocus={htmlTargetTraversal}
          onClick={(e) => {
            htmlTargetTraversal(e);
            if (props?.onClick) {
              props?.onClick();
            }
          }}
          style={props?.style}
          sx={{
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            backgroundColor: isActive ? 'white' : 'transparent',
            ...(!!editorCtx?.config?.readOnly === false &&
              !!node?.config?.readOnly === false && {
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  pointerEvents: 'none',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  border: isActive ? '2px solid #000' : 'none',
                  borderRadius: '2px',
                  boxShadow: isActive ? '0 0 0 1px rgba(0, 0, 0, 0.3)' : 'none',
                  zIndex: 1,
                },
                ...(isActive && {
                  opacity: 1,
                  position: 'relative',
                  zIndex: 5,
                }),
                '&:hover': {
                  ...(isActive
                    ? {}
                    : {
                        '&::after': {
                          border: '1px dashed #333',
                          backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        },
                      }),
                },
              }),
            ...props?.sx,
          }}
        >
          {/* @ts-ignore */}
          {node?.id && <ChildrenElementMemo child={children} />}
        </Box>
      </UiNodeContext.Provider>
    </CatchBoundary>
  );
};

const ChildrenElement = <NS extends TreeNodeState = TreeNodeState>(props: { child: Props<NS>['children'] }) => {
  const { child } = props;
  const ctx = useContext(UiNodeContext);

  if (!child) {
    return null;
  }

  if (typeof child === 'function' && ctx?.node) {
    return child({
      id: ctx.node.id,
      state: ctx?.node?.state as NS,
      setState: ctx.node.setState as TreeNode<NS>['setState'],
      setStateDebounced: ctx.node.setStateDebounced as TreeNode<NS>['setStateDebounced'],
    });
  }

  if (typeof child !== 'function') {
    return child;
  }

  return null;
};

const ChildrenElementMemo = React.memo(ChildrenElement);

/**
 * Focus on the last created node
 * - This is useful when a node is created and you want to focus a an element
 */
export const useConfigFocusOnMount = () => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const el = ref.current;
    el?.focus();

    if (el?.tagName === 'INPUT' && el?.value?.length > 0) {
      el?.select();
    }
    if (el?.tagName === 'TEXTAREA' && el?.textContent && el?.textContent?.length > 0) {
      el?.select();
    }
  }, [ref]);

  return ref;
};

/**
 * When the node is unmounted:
 * - If the node is part of a parent node with children
 * - Set the first sibling as the active node
 * - This is useful for when a node is deleted, the next node should be active
 */
export const onUnmountSetFirstSiblingActive = <NS extends TreeNodeState = TreeNodeState>(
  node: TreeNode<NS> | null,
  store: NonNullable<ReturnType<typeof useEditorContext>>['store'] | null
) => {
  if (!node) return;
  if (!store) return;

  const parentId = node?.parentId;
  if (!parentId) return;

  const state = store.getState();
  const nodes = state.nodes;

  const parent = getNodeById(parentId, nodes);
  if (!parent) return;

  if (parent.childrenNodes.size >= 1) {
    // if there are children which are not the current node, then set the first child as the active node
    parent.childrenNodes.forEach((child) => {
      if (child.id !== node.id) {
        state?.setActiveNode(child);
        return;
      }
    });
    return;
  }

  state?.setActiveNode(parent);
};

export type { Props as UiNodeProps, StylingProps as UiNodeStylingProps };

export default UiNode;
